import { money } from '@ContentManagement/EnrollmentInvoices';
import { ResponsivePie } from '@nivo/pie';
import React from 'react';
import styled from 'styled-components';
import { DistributionSlice } from '../Types/Analytics.types';

const PALETTE = [
	'#6C63FF',
	'#5AC0FC',
	'#FF8532',
	'#00CE67',
	'#E53935',
	'#FFD037',
	'#36a2eb',
	'#9c27b0',
	'#90a4ae',
];

const toPieData = (slices: DistributionSlice[]) =>
	slices
		.filter((slice) => slice.value > 0)
		.map((slice, index) => ({
			id: `${slice.name}`,
			label: slice.name,
			value: slice.value,
			color:
				slice.name === 'Other'
					? PALETTE[PALETTE.length - 1]
					: PALETTE[index % (PALETTE.length - 1)],
		}));

const Donut: React.FC<{
	title: string;
	slices: DistributionSlice[];
	isMoney?: boolean;
}> = ({ title, slices, isMoney }) => {
	const data = toPieData(slices);
	const total = data.reduce((sum, slice) => sum + slice.value, 0);
	const percent = (value: number) => (total ? `${((value / total) * 100).toFixed(1)}%` : '0%');
	const unit = isMoney ? 'EGP' : 'enrolls';
	return (
		<DonutBox>
			<DonutTitle>{title}</DonutTitle>
			{data.length ? (
				<DonutCanvas>
					<ResponsivePie
						data={data}
						innerRadius={0.5}
						padAngle={0.7}
						margin={{ top: 10, bottom: 20, left: 20, right: 20 }}
						activeOuterRadiusOffset={8}
						colors={{ datum: 'data.color' }}
						enableArcLinkLabels={false}
						isInteractive
						fit
						// "50% (500 enrolls)" — share first, absolute in brackets
						valueFormat={(value) => `${percent(Number(value))} (${money(Number(value))} ${unit})`}
						arcLabel={(datum) => percent(Number(datum.value))}
						animate
						legends={[
							{
								anchor: 'right',
								direction: 'column',
								justify: false,
								translateX: 10,
								itemsSpacing: 4,
								itemWidth: 100,
								itemHeight: 16,
								itemTextColor: '#000',
								itemDirection: 'left-to-right',
								itemOpacity: 1,
								symbolSize: 12,
								symbolShape: 'circle',
							},
						]}
					/>
				</DonutCanvas>
			) : (
				<EmptyNote>No data in the selected range</EmptyNote>
			)}
		</DonutBox>
	);
};

/** The standard distribution row: enrollments share + revenue share. */
const DistributionDonuts: React.FC<{
	enrollments: DistributionSlice[];
	revenue: DistributionSlice[];
	enrollmentsTitle?: string;
	revenueTitle?: string;
	/** when the first donut carries money too (e.g. the wallet statement pair) */
	enrollmentsIsMoney?: boolean;
}> = ({
	enrollments,
	revenue,
	enrollmentsTitle = 'Enrollments share',
	revenueTitle = 'Revenue share',
	enrollmentsIsMoney = false,
}) => (
	<DonutsRow>
		<Donut title={enrollmentsTitle} slices={enrollments} isMoney={enrollmentsIsMoney} />
		<Donut title={revenueTitle} slices={revenue} isMoney />
	</DonutsRow>
);

const DonutsRow = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
	gap: 1rem;
	width: 100%;
	margin: 2rem 0;
`;

const DonutBox = styled.div`
	background: #fff;
	border-radius: 5px;
	padding: 1rem;
	min-width: 0;
`;

const DonutTitle = styled.div`
	font-size: 1rem;
	font-weight: 600;
	margin-bottom: 0.5rem;
`;

const DonutCanvas = styled.div`
	height: 18rem;
`;

const EmptyNote = styled.div`
	color: rgba(0, 0, 0, 0.45);
	font-size: 0.875rem;
	padding: 2rem 0;
	text-align: center;
`;

export default DistributionDonuts;
